/**
 * One rate card (TMMPDM-0086; one card since TMMPDM-0089, tmm-api 0154). The page leads with the
 * margin worksheet, then Prices, Versions, Resellers (grant, revoke, Move subscribers) and the 0088
 * Controls tab, held in the URL as `tab` (the Tax tab is gone since TMMPDM-0091, tmm-api 0156);
 * the version the Worksheet and Prices read is `version`. Versions come from this card's GET;
 * every version action reads the card again. A failed re-read does not undo a successful action
 * and does not repeat it. A publish the server refuses (409 `RATE_CARD_INVALID`) opens Prices with
 * each failing field on its input.
 * Grants and adopting resellers are id lists the server sent; names come from the Partners data.
 * TMMPDM-0090 (tmm-api 0155): a version row with `violations` carries a count chip, and the publish
 * confirm states the count; Publish stays enabled, because the server decides.
 */
import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { adminApi } from '@/api/tmm/admin';
import { ApiError, errorCopy } from '@/api/errors';
import type {
  CustomerRateCardMoveResult,
  CustomerRateCardStatus,
  CustomerRateCardVersionView,
  CustomerRateCardView,
} from '@/api/tmm/types';
import {
  ConfirmDialog,
  EditDetailsDialog,
  EmptyState,
  ErrorState,
  ObjectHeader,
  SubmitButton,
  useNotify,
  type EditDetailField,
} from '@/components/page';
import { FilterTabs } from '@/components/ui';
import { useCopy } from '@/hooks/useCopy';
import { oneOf, useUrlState, type UrlSchema } from '@/hooks/useUrlState';
import { formatDate } from '@/format';
import { StatusChip } from '@/status';
import { resellerLabel, useResellerNames } from '@/routes/resellers/useResellerNames';
import {
  currentVersion,
  latestDraft,
  latestVersion,
  simpleBands,
  versionChipLabel,
  versionTerms,
} from '@/routes/customerCards/customerRateCardModel';
import { CardControlsTab } from './CardSettingsTabs';
import { CURRENCIES } from './rateCardInputs';
import { PricesTab } from './PricesTab';
import { WorksheetTab } from './WorksheetTab';
import { publishViolations, type PriceKey } from './pricing';
import { RateCardStatusChip } from './rateCardChips';

const TABS = ['worksheet', 'prices', 'versions', 'resellers', 'controls'] as const;

/** TMMPDM-0091: the Tax tab is gone (tmm-api 0156); an old `?tab=tax` link opens Controls. */
const RETIRED_TAX_TAB = 'tax';

type PageUrl = { tab: (typeof TABS)[number]; version: number | undefined };

const SCHEMA: UrlSchema<PageUrl> = {
  tab: {
    default: 'worksheet',
    parse: (raw) => (raw === RETIRED_TAX_TAB ? 'controls' : oneOf(TABS)(raw)),
  },
  version: {
    default: undefined,
    parse: (raw) => {
      const n = Number(raw);
      return Number.isSafeInteger(n) && n > 0 ? n : undefined;
    },
  },
};

function VersionStatusChip({ status }: { status: CustomerRateCardStatus }) {
  const { t } = useCopy();
  const label =
    status === 'PUBLISHED'
      ? t('customerRateCards.status.PUBLISHED', 'Published')
      : status === 'RETIRED'
        ? t('customerRateCards.status.RETIRED', 'Retired')
        : t('customerRateCards.status.DRAFT', 'Draft');
  const icon =
    status === 'PUBLISHED' ? (
      <CheckCircleOutlineIcon />
    ) : status === 'DRAFT' ? (
      <EditOutlinedIcon />
    ) : (
      <BlockIcon />
    );
  return <StatusChip status={status} icon={icon} label={label} />;
}

export function RateCardPage() {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const notify = useNotify();
  const params = useParams();
  const cardId = Number(params.id);
  const validId = Number.isSafeInteger(cardId) && cardId > 0;
  const names = useResellerNames();
  const [url, setUrl] = useUrlState(SCHEMA);
  const [searchParams] = useSearchParams();
  const retiredTab = searchParams.get('tab') === RETIRED_TAX_TAB;
  useEffect(() => {
    // Replace, so Back never returns to the retired link.
    if (retiredTab) setUrl({ tab: 'controls' }, { history: 'replace' });
  }, [retiredTab, setUrl]);
  const [card, setCard] = useState<CustomerRateCardView | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [refreshError, setRefreshError] = useState<unknown>(null);
  const [loadToken, setLoadToken] = useState(0);
  const [formGeneration, setFormGeneration] = useState(0);
  const [editOpen, setEditOpen] = useState(false);
  const [publishId, setPublishId] = useState<number | null>(null);
  const [publishNo, setPublishNo] = useState<number | null>(null);
  const [publishRefusal, setPublishRefusal] = useState<{
    versionId: number;
    versionNo: number;
    fields: Partial<Record<PriceKey, string>>;
    unplaced: boolean;
  } | null>(null);
  const [grantOpen, setGrantOpen] = useState(false);
  const [revokeId, setRevokeId] = useState<number | null>(null);
  const [moveOpen, setMoveOpen] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [versionError, setVersionError] = useState<string | null>(null);
  const [moveResult, setMoveResult] = useState<CustomerRateCardMoveResult | null>(null);

  useEffect(() => {
    if (!validId) return;
    let cancelled = false;
    adminApi
      .getCustomerRateCard(cardId)
      .then((next) => {
        if (cancelled) return;
        setCard(next);
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setCard(null);
        setError(err);
      });
    return () => {
      cancelled = true;
    };
  }, [cardId, loadToken, validId]);

  const applyCard = (next: CustomerRateCardView) => {
    setCard(next);
    setError(null);
    setRefreshError(null);
    setFormGeneration((n) => n + 1);
  };

  const reload = async () => {
    applyCard(await adminApi.getCustomerRateCard(cardId));
  };

  const reread = async () => {
    try {
      applyCard(await adminApi.getCustomerRateCard(cardId));
    } catch (err) {
      setRefreshError(err);
    }
  };

  const title = t('rateCard.singular', 'Rate card');
  const crumbs = [{ label: t('page.rateCards.title', 'Rate cards'), to: '/rate-cards' }];

  if (!validId || (error instanceof ApiError && error.status === 404)) {
    return (
      <>
        <ObjectHeader breadcrumbs={crumbs} name={title} />
        <EmptyState surface title={t('rateCard.notFound', "That rate card couldn't be found.")} />
      </>
    );
  }
  if (error) {
    return (
      <>
        <ObjectHeader breadcrumbs={crumbs} name={title} />
        <ErrorState
          error={error}
          message={t('rateCard.loadError', 'Could not load this rate card.')}
          onRetry={() => setLoadToken((n) => n + 1)}
        />
      </>
    );
  }
  if (!card) {
    return (
      <>
        <ObjectHeader breadcrumbs={crumbs} name={title} />
        <Alert severity="info">{t('common.loading', 'Loading…')}</Alert>
      </>
    );
  }

  const current = currentVersion(card);
  const draft = latestDraft(card);
  const selected =
    card.versions.find((version) => version.id === url.version) ??
    draft ??
    current ??
    latestVersion(card);
  const publishing = card.versions.find((version) => version.id === publishId) ?? null;
  // The server's count; Publish stays enabled and the server decides (TMMAPI-0155).
  const publishProblems = publishing?.violations?.length ?? 0;
  const currencyEditable = card.currentVersionId === null;
  const fields: EditDetailField[] = [
    {
      key: 'name',
      label: t('rateCards.form.name', 'Name'),
      value: card.name,
      required: true,
      maxLength: 80,
    },
  ];
  if (currencyEditable) {
    fields.push({
      key: 'currency',
      label: t('rateCards.form.currency', 'Currency'),
      value: card.currency,
      kind: 'select',
      required: true,
      options: CURRENCIES.map((code) => ({ value: code, label: code })),
    });
  }

  const openPublish = (version: CustomerRateCardVersionView) => {
    setDialogError(null);
    setPublishId(version.id);
    setPublishNo(version.versionNo);
  };

  const addVersion = async () => {
    setVersionError(null);
    const source = current ?? latestVersion(card);
    let created: CustomerRateCardVersionView;
    try {
      created = await adminApi.addCustomerRateCardVersion(
        card.id,
        source ? versionTerms(source) : undefined,
      );
    } catch (err) {
      setVersionError(
        errorCopy(err, t, t('customerRateCards.versions.newError', "Couldn't add a version.")),
      );
      return;
    }
    await reread();
    setPublishRefusal(null);
    setUrl({ tab: 'prices', version: created.id });
    notify.success(
      t('customerRateCards.toast.versionAdded', 'Version {{version, number}} is a new draft.', {
        version: created.versionNo,
      }),
    );
  };

  const publish = async () => {
    if (publishId === null) return;
    const versionId = publishId;
    const versionNo = publishing?.versionNo ?? publishNo ?? 0;
    setBusy(true);
    setDialogError(null);
    let published = false;
    try {
      await adminApi.publishCustomerRateCardVersion(card.id, versionId);
      published = true;
    } catch (err) {
      const refusing = card.versions.find((version) => version.id === versionId);
      const refusal = publishViolations(err, t, {
        guests: refusing ? simpleBands(refusing.seatBands) !== null : true,
        connections: refusing ? simpleBands(refusing.connectedAccountBands) !== null : true,
      });
      if (refusal) {
        setPublishRefusal({
          versionId,
          versionNo,
          fields: refusal.fields,
          unplaced: refusal.unplaced.length > 0 || Object.keys(refusal.fields).length === 0,
        });
        setPublishId(null);
        setBusy(false);
        setUrl({ tab: 'prices', version: versionId });
        return;
      }
      setDialogError(
        errorCopy(err, t, t('customerRateCards.errors.save', "Couldn't save this version.")),
      );
    }
    await reread();
    if (published) {
      setPublishId(null);
      setPublishRefusal(null);
      notify.success(
        t('customerRateCards.toast.published', 'Version {{version, number}} is published.', {
          version: versionNo,
        }),
      );
    }
    setBusy(false);
  };

  const move = async () => {
    setBusy(true);
    setDialogError(null);
    let result: CustomerRateCardMoveResult;
    try {
      result = await adminApi.moveCustomerRateCardSubscribers(card.id);
    } catch (err) {
      setDialogError(
        errorCopy(err, t, t('customerRateCards.move.error', "Couldn't move subscribers.")),
      );
      setBusy(false);
      return;
    }
    await reread();
    setMoveResult(result);
    setMoveOpen(false);
    setBusy(false);
  };

  const revoke = async () => {
    if (revokeId === null) return;
    setBusy(true);
    setDialogError(null);
    try {
      const next = await adminApi.revokeCustomerRateCard(card.id, revokeId);
      setCard(next);
      setRevokeId(null);
      notify.success(t('customerRateCards.toast.revoked', 'Grant revoked.'));
    } catch (err) {
      const reseller = resellerLabel(names.names, revokeId);
      setDialogError(
        err instanceof ApiError && String(err.code) === 'CARD_ADOPTED'
          ? t(
              'customerRateCards.revoke.adopted',
              '{{reseller}} uses this card. It must adopt another before this grant can be revoked.',
              { reseller },
            )
          : errorCopy(err, t, t('customerRateCards.revoke.error', "Couldn't revoke this grant.")),
      );
    } finally {
      setBusy(false);
    }
  };

  const closeDialog = () => {
    if (busy) return;
    setPublishId(null);
    setMoveOpen(false);
    setRevokeId(null);
    setDialogError(null);
  };

  const showVersionPicker = url.tab === 'worksheet' || url.tab === 'prices';

  return (
    <Stack spacing={2}>
      <ObjectHeader
        breadcrumbs={crumbs}
        name={card.name}
        status={
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <RateCardStatusChip status={card.status} />
            <Typography component="span" variant="body2">
              {card.currency}
              {current
                ? ` · ${versionChipLabel(current.versionNo, t)}`
                : ` · ${t('rateCard.draftOnly', 'draft')}`}
            </Typography>
          </Stack>
        }
        {...(draft
          ? {
              primaryAction: {
                label: t(
                  'rateCard.actions.publishVersion',
                  'Publish version {{version, number}}…',
                  {
                    version: draft.versionNo,
                  },
                ),
                onClick: () => openPublish(draft),
              },
            }
          : {})}
        secondaryActions={[
          {
            label: t('customerRateCards.actions.editDetails', 'Edit details…'),
            onClick: () => setEditOpen(true),
          },
          {
            label: t('customerRateCards.actions.newVersion', 'New version…'),
            onClick: () => void addVersion(),
          },
          ...(current
            ? [
                {
                  label: t('customerRateCards.actions.move', 'Move subscribers…'),
                  onClick: () => {
                    setDialogError(null);
                    setMoveOpen(true);
                  },
                },
              ]
            : []),
        ]}
      />
      {refreshError ? (
        <ErrorState
          error={refreshError}
          message={t('rateCard.loadError', 'Could not load this rate card.')}
          onRetry={() => {
            void reload().catch((err: unknown) => setRefreshError(err));
          }}
        />
      ) : null}
      {versionError ? <Alert severity="error">{versionError}</Alert> : null}
      {moveResult ? (
        <Alert severity={moveResult.failed > 0 ? 'warning' : 'success'}>
          {t(
            'customerRateCards.move.result',
            'Moved {{moved, number}}. Already on this version: {{already, number}}. Failed: {{failed, number}}.',
            {
              moved: moveResult.moved,
              already: moveResult.alreadyCurrent,
              failed: moveResult.failed,
            },
          )}
          {moveResult.failed > 0
            ? ` ${t('customerRateCards.move.failedWarning', 'Some subscribers were not moved.')}`
            : ''}
        </Alert>
      ) : null}
      <FilterTabs
        ariaLabel={t('rateCard.tabs.aria', 'Rate card sections')}
        active={url.tab}
        onChange={(tab) => {
          if ((TABS as readonly string[]).includes(tab))
            setUrl({ tab: tab as (typeof TABS)[number] });
        }}
        tabs={[
          { key: 'worksheet', label: t('rateCard.tabs.worksheet', 'Worksheet') },
          { key: 'prices', label: t('rateCard.tabs.prices', 'Prices') },
          { key: 'versions', label: t('customerRateCards.tabs.versions', 'Versions') },
          { key: 'resellers', label: t('rateCard.tabs.resellers', 'Resellers') },
          { key: 'controls', label: t('customerRateCards.tabs.controls', 'Controls') },
        ]}
      />
      {showVersionPicker && selected && card.versions.length > 1 ? (
        <TextField
          select
          size="small"
          sx={{ maxWidth: '28ch' }}
          label={t('rateCard.versionPicker', 'Version')}
          value={String(selected.id)}
          onChange={(event) => setUrl({ version: Number(event.target.value) })}
        >
          {[...card.versions]
            .sort((a, b) => b.versionNo - a.versionNo)
            .map((version) => (
              <MenuItem key={version.id} value={String(version.id)}>
                {`${versionChipLabel(version.versionNo, t)} · ${
                  version.status === 'PUBLISHED'
                    ? t('customerRateCards.status.PUBLISHED', 'Published')
                    : version.status === 'RETIRED'
                      ? t('customerRateCards.status.RETIRED', 'Retired')
                      : t('customerRateCards.status.DRAFT', 'Draft')
                }`}
              </MenuItem>
            ))}
        </TextField>
      ) : null}
      {url.tab === 'worksheet' ? (
        selected ? (
          <WorksheetTab
            key={selected.id}
            cardId={card.id}
            currency={card.currency}
            version={selected}
          />
        ) : (
          <EmptyState surface title={t('customerRateCards.versions.empty', 'No versions yet.')} />
        )
      ) : url.tab === 'prices' ? (
        selected ? (
          <Stack spacing={2}>
            {publishRefusal && publishRefusal.versionId === selected.id ? (
              <Alert severity="error">
                {Object.keys(publishRefusal.fields).length > 0
                  ? t(
                      'rateCard.publishRefused',
                      "Version {{version, number}} can't be published yet. Fix the marked prices, save the draft, then publish.",
                      { version: publishRefusal.versionNo },
                    )
                  : t(
                      'rateCard.publishRefusedNoField',
                      "Version {{version, number}} can't be published yet.",
                      { version: publishRefusal.versionNo },
                    )}
                {publishRefusal.unplaced
                  ? ` ${t('rateCard.publishRefusedOther', 'Some of the checks that failed have no field on this page.')}`
                  : ''}
              </Alert>
            ) : null}
            <PricesTab
              key={`${selected.id}:${formGeneration}`}
              cardId={card.id}
              currency={card.currency}
              version={selected}
              {...(publishRefusal && publishRefusal.versionId === selected.id
                ? { serverErrors: publishRefusal.fields }
                : {})}
              onSaved={() => {
                setPublishRefusal(null);
                notify.success(t('rateCard.toast.saved', 'Prices saved.'));
                void reload().catch((err: unknown) => setRefreshError(err));
              }}
              onReload={() => {
                void reload().catch((err: unknown) => setRefreshError(err));
              }}
            />
          </Stack>
        ) : (
          <EmptyState surface title={t('customerRateCards.versions.empty', 'No versions yet.')} />
        )
      ) : url.tab === 'versions' ? (
        card.versions.length === 0 ? (
          <EmptyState surface title={t('customerRateCards.versions.empty', 'No versions yet.')} />
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" aria-label={t('customerRateCards.versions.tableAria', 'Versions')}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('customerRateCards.versions.version', 'Version')}</TableCell>
                  <TableCell>{t('customerRateCards.versions.state', 'State')}</TableCell>
                  <TableCell>{t('customerRateCards.versions.published', 'Published')}</TableCell>
                  <TableCell align="right">
                    {t('customerRateCards.versions.subscribers', 'Subscribers')}
                  </TableCell>
                  <TableCell>{t('resellerCards.col.actions', 'Actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[...card.versions]
                  .sort((a, b) => b.versionNo - a.versionNo)
                  .map((version) => (
                    <TableRow key={version.id}>
                      <TableCell>{versionChipLabel(version.versionNo, t)}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                          <VersionStatusChip status={version.status} />
                          {version.current || version.id === card.currentVersionId ? (
                            <StatusChip
                              tone="info"
                              label={t('customerRateCards.chip.current', 'Current')}
                            />
                          ) : null}
                          {(version.violations ?? []).length > 0 ? (
                            <StatusChip
                              tone="warning"
                              icon={<WarningAmberIcon />}
                              label={t('rateCard.violations.chip', '{{count, number}} problems', {
                                count: (version.violations ?? []).length,
                              })}
                            />
                          ) : null}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {version.activatedAt === null
                          ? '—'
                          : formatDate(version.activatedAt, i18n.language)}
                      </TableCell>
                      <TableCell align="right">{version.subscriberCount}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <Button
                            size="small"
                            onClick={() => setUrl({ tab: 'worksheet', version: version.id })}
                          >
                            {t('rateCard.actions.worksheet', 'Worksheet')}
                          </Button>
                          {version.status === 'DRAFT' ? (
                            <>
                              <Button
                                size="small"
                                onClick={() => setUrl({ tab: 'prices', version: version.id })}
                              >
                                {t('rateCard.actions.editPrices', 'Edit prices…')}
                              </Button>
                              <Button size="small" onClick={() => openPublish(version)}>
                                {t('customerRateCards.actions.publish', 'Publish…')}
                              </Button>
                            </>
                          ) : null}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        )
      ) : url.tab === 'controls' ? (
        <CardControlsTab cardId={card.id} />
      ) : (
        <ResellersSection
          card={card}
          names={names.names}
          resellers={names.resellers}
          resellerStatus={names.status}
          resellerError={names.error}
          onResellerRetry={names.retry}
          onGrant={() => {
            setDialogError(null);
            setGrantOpen(true);
          }}
          onRevoke={(resellerId) => {
            setDialogError(null);
            setRevokeId(resellerId);
          }}
          grantOpen={grantOpen}
          onGrantClose={() => {
            if (!busy) {
              setGrantOpen(false);
              setDialogError(null);
            }
          }}
          busy={busy}
          dialogError={dialogError}
          onGranted={(next, grantedId) => {
            setCard(next);
            setGrantOpen(false);
            setDialogError(null);
            notify.success(
              t('customerRateCards.toast.granted', 'Granted to {{reseller}}.', {
                reseller: resellerLabel(names.names, grantedId),
              }),
            );
          }}
          setBusy={setBusy}
          setDialogError={setDialogError}
        />
      )}
      <EditDetailsDialog
        open={editOpen}
        entityLabel={title}
        fields={fields}
        version={card.version}
        reloadToken={formGeneration}
        onClose={() => setEditOpen(false)}
        onReload={reload}
        onSubmit={async (changed) => {
          const next = await adminApi.patchCustomerRateCard(card.id, {
            ...(changed.name !== undefined ? { name: changed.name } : {}),
            ...(changed.currency !== undefined ? { currency: changed.currency } : {}),
          });
          setCard(next);
        }}
      />
      <ConfirmDialog
        open={publishId !== null}
        title={t('customerRateCards.publish.title', 'Publish version {{version, number}}', {
          version: publishing?.versionNo ?? publishNo ?? 0,
        })}
        consequence={
          <>
            {t(
              'customerRateCards.publish.consequence',
              'Publish version {{version, number}}? New sales use it. Existing subscribers stay on their version until you move them.',
              { version: publishing?.versionNo ?? publishNo ?? 0 },
            )}
            {publishProblems > 0
              ? ` ${t(
                  'rateCard.violations.summary',
                  "This version has {{count, number}} problems; publishing will be refused until they're fixed.",
                  { count: publishProblems },
                )}`
              : null}
          </>
        }
        confirmLabel={t('customerRateCards.publish.confirm', 'Publish')}
        busy={busy}
        error={dialogError}
        onConfirm={() => void publish()}
        onCancel={closeDialog}
      />
      <ConfirmDialog
        open={moveOpen && current !== undefined}
        title={t('customerRateCards.move.title', 'Move subscribers')}
        consequence={t(
          'customerRateCards.move.consequence',
          "Move this card's subscribers on older versions to version {{version, number}}? Each one moved is sent a notice.",
          { version: current?.versionNo ?? 0 },
        )}
        confirmLabel={t('customerRateCards.move.confirm', 'Move subscribers')}
        busy={busy}
        error={dialogError}
        onConfirm={() => void move()}
        onCancel={closeDialog}
      />
      <ConfirmDialog
        open={revokeId !== null}
        title={t('customerRateCards.revoke.title', 'Revoke grant')}
        consequence={t(
          'customerRateCards.revoke.consequence',
          'Revoke this grant for {{reseller}}?',
          { reseller: revokeId === null ? '' : resellerLabel(names.names, revokeId) },
        )}
        confirmLabel={t('customerRateCards.revoke.confirm', 'Revoke')}
        destructive
        busy={busy}
        error={dialogError}
        onConfirm={() => void revoke()}
        onCancel={closeDialog}
      />
    </Stack>
  );
}

function ResellersSection({
  card,
  names,
  resellers,
  resellerStatus,
  resellerError,
  onResellerRetry,
  onGrant,
  onRevoke,
  grantOpen,
  onGrantClose,
  busy,
  dialogError,
  onGranted,
  setBusy,
  setDialogError,
}: {
  card: CustomerRateCardView;
  names: Map<number, string>;
  resellers: { id: number; name: string }[];
  resellerStatus: 'loading' | 'ready' | 'failed';
  resellerError: unknown;
  onResellerRetry: () => void;
  onGrant: () => void;
  onRevoke: (resellerId: number) => void;
  grantOpen: boolean;
  onGrantClose: () => void;
  busy: boolean;
  dialogError: string | null;
  onGranted: (card: CustomerRateCardView, resellerId: number) => void;
  setBusy: (busy: boolean) => void;
  setDialogError: (error: string | null) => void;
}) {
  const { t } = useCopy();
  const [picked, setPicked] = useState<number | null>(null);
  const partnersReady = resellerStatus === 'ready';
  const available = partnersReady
    ? resellers.filter((reseller) => !card.grants.includes(reseller.id))
    : [];
  const resellerId =
    picked !== null && available.some((reseller) => reseller.id === picked)
      ? picked
      : (available[0]?.id ?? null);

  const grant = async () => {
    if (resellerId === null) return;
    setBusy(true);
    setDialogError(null);
    try {
      const next = await adminApi.grantCustomerRateCard(card.id, resellerId);
      onGranted(next, resellerId);
    } catch (err) {
      setDialogError(
        errorCopy(err, t, t('rateCard.grant.error', "Couldn't grant this rate card.")),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack spacing={2}>
      {card.currentVersionId === null ? (
        <Alert severity="info">
          {t(
            'customerRateCards.grants.unpublished',
            "Resellers can't see this card until a version is published.",
          )}
        </Alert>
      ) : null}
      {resellerStatus === 'failed' ? (
        <ErrorState
          error={resellerError}
          message={t('resellers.loadError', 'Could not load resellers.')}
          onRetry={onResellerRetry}
        />
      ) : null}
      <Typography variant="body2" color="text.secondary">
        {t('rateCard.resellers.adoptHint', 'A reseller adopts a granted card on its partner page.')}
      </Typography>
      <Button sx={{ alignSelf: 'flex-start' }} onClick={onGrant}>
        {t('customerRateCards.actions.grant', 'Grant to reseller…')}
      </Button>
      {card.grants.length === 0 ? (
        <EmptyState
          surface
          title={t('customerRateCards.grants.empty', 'No reseller has this card.')}
        />
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" aria-label={t('rateCard.tabs.resellers', 'Resellers')}>
            <TableHead>
              <TableRow>
                <TableCell>{t('customerRateCards.grants.reseller', 'Reseller')}</TableCell>
                <TableCell>{t('customerRateCards.grants.adoption', 'Adoption')}</TableCell>
                <TableCell>{t('resellerCards.col.actions', 'Actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {card.grants.map((id) => {
                const adopted = card.adoptingResellers.includes(id);
                return (
                  <TableRow key={id}>
                    <TableCell>
                      <Link component={RouterLink} to={`/partners/${id}`}>
                        {resellerLabel(names, id)}
                      </Link>
                    </TableCell>
                    <TableCell>
                      {adopted
                        ? t('customerRateCards.grants.adopted', 'Adopts this card')
                        : t('customerRateCards.grants.notAdopted', 'Granted')}
                    </TableCell>
                    <TableCell>
                      <Button size="small" onClick={() => onRevoke(id)}>
                        {t('customerRateCards.actions.revoke', 'Revoke…')}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Dialog open={grantOpen} onClose={onGrantClose} aria-labelledby="grant-rate-card-title">
        <DialogTitle id="grant-rate-card-title">
          {t('customerRateCards.grant.title', 'Grant to reseller')}
        </DialogTitle>
        <DialogContent>
          {card.currentVersionId === null ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t(
                'customerRateCards.grants.unpublished',
                "Resellers can't see this card until a version is published.",
              )}
            </Alert>
          ) : null}
          {resellerStatus === 'loading' ? (
            <Alert severity="info">{t('common.loading', 'Loading…')}</Alert>
          ) : resellerStatus === 'failed' ? (
            <ErrorState
              error={resellerError}
              message={t('resellers.loadError', 'Could not load resellers.')}
              onRetry={onResellerRetry}
            />
          ) : available.length === 0 ? (
            <Typography>
              {t('customerRateCards.grant.empty', 'Every reseller already has this card.')}
            </Typography>
          ) : (
            <TextField
              select
              fullWidth
              margin="normal"
              label={t('customerRateCards.grant.reseller', 'Reseller')}
              value={resellerId === null ? '' : String(resellerId)}
              onChange={(event) => setPicked(Number(event.target.value))}
            >
              {available.map((reseller) => (
                <MenuItem key={reseller.id} value={String(reseller.id)}>
                  {reseller.name}
                </MenuItem>
              ))}
            </TextField>
          )}
          {dialogError ? (
            <Alert severity="error" sx={{ mt: 1 }}>
              {dialogError}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={onGrantClose} disabled={busy}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <SubmitButton
            busy={busy}
            disabled={!partnersReady || available.length === 0 || resellerId === null}
            onClick={() => void grant()}
          >
            {t('customerRateCards.grant.confirm', 'Grant')}
          </SubmitButton>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
